import React, { useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import api, { resolveUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Reusable image uploader. Stores relative URL like /api/uploads/xxx.png.
// Displays absolute preview through resolveUrl.
export default function ImageUploader({ value, onChange, label = 'Image', hint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const { toast } = useToast();

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast({ title: 'Too large', description: 'Max 8 MB.' });
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
      toast({ title: 'Uploaded', description: file.name });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      {label && <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">{label}</label>}
      <div className="flex items-start gap-3">
        {value ? (
          <div className="relative">
            <img src={resolveUrl(value)} alt="preview" className="w-24 h-24 rounded-xl object-cover border border-gray-200" data-testid="uploader-preview" />
            <button type="button" onClick={() => onChange('')} data-testid="uploader-clear"
                    className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50 hover:text-red-600">
              <X size={12} />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-xl bg-[#FDF3EC] flex items-center justify-center text-gray-400 border border-dashed border-gray-300">
            <Upload size={22} />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                  data-testid="uploader-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0F1C2E] text-white text-sm font-semibold hover:bg-[#ED2E2E] transition disabled:opacity-50">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
          </button>
          <input ref={inputRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="uploader-input" />
          <div className="text-xs text-gray-500">
            {hint || 'PNG, JPG, WEBP up to 8 MB.'}
            <div className="text-gray-400 mt-1">Or paste a URL:</div>
          </div>
          <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="/api/uploads/... or https://..."
                 data-testid="uploader-url"
                 className="w-full text-xs px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#ED2E2E]" />
        </div>
      </div>
    </div>
  );
}
